import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Loader2, Search, User, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDeals } from "@/hooks/useDeals";
import { useDealItems } from "@/hooks/useDealItems";
import {
  DealCommissionFields,
  emptyDealCommission,
  toCommissionPayload,
  type DealCommissionFieldState,
} from "./DealCommissionFields";
import type { DealStage } from "@/types/deal";

interface LeadOption {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

interface ProductOption {
  id: string;
  name: string;
  price: number | null;
  sku: string | null;
}

interface DealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  stages: DealStage[];
  memberId?: string | null;
  onCreated: () => void;
  initialLeadId?: string;
  initialLeadName?: string;
  lockLead?: boolean;
}

export function DealDialog({
  open, onOpenChange, workspaceId, stages, memberId, onCreated,
  initialLeadId, initialLeadName, lockLead,
}: DealDialogProps) {
  const [title, setTitle] = useState("");
  const [stageId, setStageId] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [commission, setCommission] = useState<DealCommissionFieldState>(emptyDealCommission);
  const [members, setMembers] = useState<{ id: string; display_name: string | null }[]>([]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Lead search
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const [leadSearchTerm, setLeadSearchTerm] = useState("");
  const [leadResults, setLeadResults] = useState<LeadOption[]>([]);
  const [leadSearching, setLeadSearching] = useState(false);
  const [leadPopoverOpen, setLeadPopoverOpen] = useState(false);
  const leadDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Product selector
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState<ProductOption[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const productDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { createDealFromLead } = useDeals(workspaceId);
  const { addItem } = useDealItems(null);

  const openStages = stages.filter(s => !s.is_closed_won && !s.is_closed_lost);

  // Reset form on open
  useEffect(() => {
    if (open) {
      setTitle("");
      setStageId("");
      setManualAmount("");
      setNotes("");
      setCommission(emptyDealCommission);
      setSelectedProduct(null);
      setProductSearch("");
      setProductResults([]);
      setLeadSearchTerm("");
      setLeadResults([]);

      if (initialLeadId && initialLeadName) {
        setSelectedLead({ id: initialLeadId, name: initialLeadName, phone: null, email: null });
      } else {
        setSelectedLead(null);
      }
    }
  }, [open, initialLeadId, initialLeadName]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as { id: string; display_name: string | null }[]);
    })();
  }, [open, workspaceId]);

  // Lead search with debounce
  const searchLeads = useCallback(async (term: string) => {
    if (!workspaceId || term.length < 3) {
      setLeadResults([]);
      setLeadSearching(false);
      return;
    }
    setLeadSearching(true);
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("id, name, phone, email")
        .eq("workspace_id", workspaceId)
        .or(`name.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(20);
      if (error) throw error;
      setLeadResults((data || []) as LeadOption[]);
    } catch {
      setLeadResults([]);
    } finally {
      setLeadSearching(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current);
    if (leadSearchTerm.trim().length < 3) {
      setLeadResults([]);
      return;
    }
    leadDebounceRef.current = setTimeout(() => searchLeads(leadSearchTerm.trim()), 400);
    return () => { if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current); };
  }, [leadSearchTerm, searchLeads]);

  // Product search with debounce
  const searchProducts = useCallback(async (term: string) => {
    if (!workspaceId) return;
    setProductSearching(true);
    try {
      let q = supabase
        .from("products")
        .select("id, name, price, sku")
        .eq("workspace_id", workspaceId)
        .limit(20);
      if (term.length >= 2) {
        q = q.or(`name.ilike.%${term}%,sku.ilike.%${term}%`);
      }
      const { data, error } = await q.order("name");
      if (error) throw error;
      setProductResults((data || []) as ProductOption[]);
    } catch {
      setProductResults([]);
    } finally {
      setProductSearching(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
    if (!productPopoverOpen) return;
    productDebounceRef.current = setTimeout(() => searchProducts(productSearch.trim()), 300);
    return () => { if (productDebounceRef.current) clearTimeout(productDebounceRef.current); };
  }, [productSearch, productPopoverOpen, searchProducts]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: "Título obrigatório", variant: "destructive" });
      return;
    }
    if (!selectedLead) {
      toast({ title: "Selecione um lead", variant: "destructive" });
      return;
    }

    const selectedStage = stageId ? stages.find(s => s.id === stageId) : openStages[0];
    if (!selectedStage) return;

    setSaving(true);
    try {
      // Step 1: Create deal via RPC
      const dealId = await createDealFromLead.mutateAsync({
        workspaceId,
        leadId: selectedLead.id,
        title: title.trim(),
        memberId: memberId ?? undefined,
      });

      const { error: updateError } = await supabase.from("deals").update({
        stage_id: selectedStage.id,
        probability: selectedStage.probability,
        manual_amount: manualAmount ? parseFloat(manualAmount) : 0,
        notes: notes || null,
        ...toCommissionPayload(commission),
      }).eq("id", dealId);
      if (updateError) throw updateError;

      // Step 2: Add product item if selected (failure tolerated)
      if (selectedProduct && dealId) {
        try {
          await addItem.mutateAsync({
            deal_id: dealId,
            workspace_id: workspaceId,
            product_id: selectedProduct.id,
            product_name_snapshot: selectedProduct.name,
            unit_price: selectedProduct.price ?? 0,
            quantity: 1,
            sku_snapshot: selectedProduct.sku || null,
          });
        } catch {
          toast({
            title: "Produto não adicionado",
            description: "A negociação foi criada, mas o produto não foi adicionado. Você pode adicioná-lo depois.",
            variant: "destructive",
          });
        }
      }

      onCreated();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Erro", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Negociação</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Lead selector */}
          <div className="space-y-2">
            <Label>Lead *</Label>
            {lockLead && selectedLead ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{selectedLead.name}</span>
                {selectedLead.phone && <span className="text-muted-foreground">· {selectedLead.phone}</span>}
              </div>
            ) : (
              <Popover open={leadPopoverOpen} onOpenChange={setLeadPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn("w-full justify-start text-left font-normal", !selectedLead && "text-muted-foreground")}
                  >
                    {selectedLead ? (
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {selectedLead.name}
                        {selectedLead.phone && <span className="text-muted-foreground text-xs">· {selectedLead.phone}</span>}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Buscar lead...
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Digite 3+ caracteres..."
                      value={leadSearchTerm}
                      onValueChange={setLeadSearchTerm}
                    />
                    <CommandList>
                      {leadSearching && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {!leadSearching && leadSearchTerm.length >= 3 && leadResults.length === 0 && (
                        <CommandEmpty>Nenhum lead encontrado</CommandEmpty>
                      )}
                      {!leadSearching && leadSearchTerm.length < 3 && leadSearchTerm.length > 0 && (
                        <div className="py-3 text-center text-xs text-muted-foreground">
                          Digite pelo menos 3 caracteres
                        </div>
                      )}
                      <CommandGroup>
                        {leadResults.map((lead) => (
                          <CommandItem
                            key={lead.id}
                            value={lead.id}
                            onSelect={() => {
                              setSelectedLead(lead);
                              setLeadPopoverOpen(false);
                              if (!title.trim()) {
                                setTitle(`Negociação - ${lead.name}`);
                              }
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedLead?.id === lead.id ? "opacity-100" : "opacity-0")} />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{lead.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {[lead.phone, lead.email].filter(Boolean).join(" · ") || "Sem contato"}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Proposta cliente X" />
          </div>

          {/* Stage */}
          <div className="space-y-2">
            <Label>Estágio</Label>
            <Select value={stageId || openStages[0]?.id || ""} onValueChange={setStageId}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {openStages.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.probability}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label>Valor (R$)</Label>
            <Input type="number" min={0} step={0.01} value={manualAmount} onChange={e => setManualAmount(e.target.value)} placeholder="0,00" />
          </div>

          <DealCommissionFields members={members} value={commission} onChange={setCommission} />

          {/* Product selector (optional) */}
          <div className="space-y-2">
            <Label>Produto Inicial (opcional)</Label>
            <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn("w-full justify-start text-left font-normal", !selectedProduct && "text-muted-foreground")}
                >
                  {selectedProduct ? selectedProduct.name : "Selecionar produto do catálogo..."}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Buscar produto..."
                    value={productSearch}
                    onValueChange={setProductSearch}
                  />
                  <CommandList>
                    {productSearching && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {!productSearching && productResults.length === 0 && (
                      <CommandEmpty>Nenhum produto encontrado</CommandEmpty>
                    )}
                    <CommandGroup>
                      {productResults.map((p) => (
                        <CommandItem
                          key={p.id}
                          value={p.id}
                          onSelect={() => {
                            setSelectedProduct(p);
                            setProductPopoverOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", selectedProduct?.id === p.id ? "opacity-100" : "opacity-0")} />
                          <div className="flex flex-col">
                            <span className="text-sm">{p.name}</span>
                            {p.price != null && (
                              <span className="text-xs text-muted-foreground">
                                R$ {Number(p.price).toFixed(2)}
                                {p.sku && ` · ${p.sku}`}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedProduct && (
              <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground" onClick={() => setSelectedProduct(null)}>
                Remover produto
              </Button>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>

          <Button className="w-full" onClick={handleSubmit} disabled={saving}>
            {saving ? "Criando..." : "Criar Negociação"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
